import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, ArrowLeft, Check } from 'lucide-react';
import { authRegister, authLogin, getGoogleAuthUrl, companyLookup } from '../../config';
import { useAuth } from '../../context/AuthContext';

// Vague 4 (2026-05-30) : connexion email + mot de passe (en plus de Google SSO).
// L'inscription collecte les infos « réception client » ; l'adresse est optionnelle.
export default function AccountLogin({ mode = 'login' }) {
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const { user, loading: authLoading, loginWithSession } = useAuth();
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '',
    customer_type: 'particulier', company_name: '', siret: '', vat_number: '',
    address: '', postal_code: '', city: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState({ status: 'idle', name: '' }); // idle | loading | found | notfound

  useEffect(() => {
    if (!authLoading && user) navigate('/compte', { replace: true });
  }, [authLoading, user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isPro = form.customer_type === 'professionnel';

  // Auto-remplissage de la fiche société à partir du SIRET (API officielle).
  const runSiretLookup = async (rawSiret) => {
    const digits = (rawSiret || '').replace(/\D/g, '');
    if (digits.length !== 14) { setLookup({ status: 'idle', name: '' }); return; }
    setLookup({ status: 'loading', name: '' });
    try {
      const res = await companyLookup(digits);
      if (res?.found && res.company) {
        const c = res.company;
        setForm((f) => ({
          ...f,
          company_name: c.name || f.company_name,
          vat_number: c.vat_number || '',
          address: c.address || f.address,
          postal_code: c.postal_code || f.postal_code,
          city: c.city || f.city,
        }));
        setLookup({ status: 'found', name: c.name || '' });
      } else {
        setLookup({ status: 'notfound', name: '' });
      }
    } catch {
      setLookup({ status: 'notfound', name: '' });
    }
  };
  const handleSiretChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, siret: v }));
    if (v.replace(/\D/g, '').length === 14) runSiretLookup(v);
    else setLookup({ status: 'idle', name: '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    if (isSignup && isPro && (form.siret || '').replace(/\D/g, '').length !== 14) {
      setError('Le SIRET (14 chiffres) est obligatoire pour un compte professionnel.');
      setSubmitting(false);
      return;
    }
    try {
      const data = isSignup ? await authRegister(form) : await authLogin(form.email, form.password);
      await loginWithSession(data.session_token, data);
      navigate('/compte', { replace: true });
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const data = await getGoogleAuthUrl(`${window.location.origin}/my`, 'client');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Erreur de connexion Google');
      setGoogleLoading(false);
    }
  };

  const input = 'w-full bg-white border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-slate-900 focus:border-secondary focus:ring-1 focus:ring-secondary/30 outline-none';
  const label = 'block font-accent text-[11px] uppercase tracking-widest text-slate-500 mb-1';

  return (
    <div className="min-h-screen bg-primary text-white flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <img src="/logo-meteris.png" alt="Météris" className="h-16 w-16 rounded-full object-cover ring-2 ring-secondary/30" />
          </Link>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {isSignup ? 'Créer mon compte' : 'Connexion'}
          </h1>
        </div>

        <div className="bg-slate-50 text-slate-900 rounded-sm border border-slate-200 shadow-2xl p-6 md:p-8 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Prénom</label>
                    <input className={input} required value={form.first_name} onChange={set('first_name')} />
                  </div>
                  <div>
                    <label className={label}>Nom</label>
                    <input className={input} required value={form.last_name} onChange={set('last_name')} />
                  </div>
                </div>
                <div>
                  <label className={label}>Téléphone</label>
                  <input className={input} type="tel" required value={form.phone} onChange={set('phone')} />
                </div>
                <div>
                  <label className={label}>Vous êtes</label>
                  <div className="flex gap-2">
                    {[['particulier', 'Particulier'], ['professionnel', 'Professionnel']].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, customer_type: v }))}
                        className={`flex-1 py-2 rounded-sm text-sm font-medium border transition-colors ${
                          form.customer_type === v
                            ? 'bg-secondary/10 text-secondary border-secondary/40'
                            : 'bg-white text-slate-600 border-slate-200'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {isPro && (
                  <div className="space-y-3 rounded-sm border border-secondary/20 bg-secondary/5 p-3">
                    <div>
                      <label className={label}>SIRET <span className="text-rose-500 normal-case">*</span></label>
                      <input
                        className={input}
                        inputMode="numeric"
                        placeholder="14 chiffres — la fiche société se remplit toute seule"
                        required
                        value={form.siret}
                        onChange={handleSiretChange}
                        onBlur={(e) => runSiretLookup(e.target.value)}
                      />
                      <div className="mt-1 min-h-[18px] text-xs flex items-center gap-1.5">
                        {lookup.status === 'loading' && <span className="text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Recherche de l'entreprise…</span>}
                        {lookup.status === 'found' && <span className="text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {lookup.name}</span>}
                        {lookup.status === 'notfound' && <span className="text-amber-600">Entreprise introuvable — vérifie le SIRET (tu peux saisir la société à la main).</span>}
                      </div>
                    </div>
                    <div>
                      <label className={label}>Société <span className="text-rose-500 normal-case">*</span></label>
                      <input className={input} required value={form.company_name} onChange={set('company_name')} placeholder="Raison sociale" />
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <label className={label}>Email</label>
              <input className={input} type="email" required value={form.email} onChange={set('email')} />
            </div>
            <div>
              <label className={label}>
                Mot de passe {isSignup && <span className="text-slate-400 normal-case">(8 caractères min.)</span>}
              </label>
              <input
                className={input}
                type="password"
                required
                minLength={isSignup ? 8 : undefined}
                value={form.password}
                onChange={set('password')}
              />
            </div>

            {!isSignup && (
              <div className="text-right -mt-2">
                <Link to="/compte/mot-de-passe-oublie" className="text-xs text-slate-500 hover:text-secondary transition-colors">
                  Mot de passe oublié ?
                </Link>
              </div>
            )}

            {isSignup && (
              <details className="text-sm">
                <summary className="cursor-pointer font-accent text-[11px] uppercase tracking-widest text-slate-500 hover:text-secondary">
                  Adresse (optionnel)
                </summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className={label}>Adresse</label>
                    <input className={input} value={form.address} onChange={set('address')} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Code postal</label>
                      <input className={input} value={form.postal_code} onChange={set('postal_code')} />
                    </div>
                    <div>
                      <label className={label}>Ville</label>
                      <input className={input} value={form.city} onChange={set('city')} />
                    </div>
                  </div>
                </div>
              </details>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-secondary text-white hover:bg-secondary/90 font-bold uppercase tracking-wider rounded-sm py-3 text-sm transition-all active:scale-95 disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
              {isSignup ? 'Créer mon compte' : 'Se connecter'}
            </button>
          </form>

          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400">
            <span className="flex-1 h-px bg-slate-200" />
            ou
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={googleLogin}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm transition-all active:scale-95 disabled:opacity-60"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            <span>Continuer avec Google</span>
          </button>

          <p className="text-xs text-slate-500 text-center">
            {isSignup ? 'Déjà un compte ? ' : 'Pas encore de compte ? '}
            <Link
              to={isSignup ? '/compte/connexion' : '/compte/inscription'}
              className="font-accent uppercase tracking-wider text-secondary hover:text-primary transition-colors"
            >
              {isSignup ? 'Se connecter' : 'Créer mon compte'}
            </Link>
          </p>
        </div>

        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 font-accent text-xs uppercase tracking-widest text-slate-400 hover:text-secondary transition-colors w-full justify-center"
        >
          <ArrowLeft className="w-3 h-3" />
          Retour au site
        </Link>
      </div>
    </div>
  );
}
